import { useMutation, useQueryClient } from "@tanstack/react-query";
import { departmentService } from "@/features/departments/services/departmentService";
import { handleApiError, toastMutationSuccess } from "@/lib/error-handler";
import { queryKeys } from "@/lib/query-keys";
import type {
  AssignManagerRequest,
  CreateDepartmentRequest,
  Department,
  UpdateDepartmentRequest,
  UpdateDepartmentSettingsRequest,
} from "@/types/department";

function invalidateDepartments(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: queryKeys.departments.all });
}

export function useCreateDepartment() {
  const queryClient = useQueryClient();
  return useMutation<Department, Error, CreateDepartmentRequest>({
    mutationFn: (data) => departmentService.create(data),
    onSuccess: (res) => {
      invalidateDepartments(queryClient);
      toastMutationSuccess(res, { fallbackMessage: "تم إنشاء الدائرة بنجاح" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();
  return useMutation<
    Department,
    Error,
    { id: string; data: UpdateDepartmentRequest }
  >({
    mutationFn: ({ id, data }) => departmentService.update(id, data),
    onSuccess: (res) => {
      invalidateDepartments(queryClient);
      toastMutationSuccess(res, { fallbackMessage: "تم تحديث الدائرة بنجاح" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useUpdateDepartmentSettings() {
  const queryClient = useQueryClient();
  return useMutation<
    Department,
    Error,
    { id: string; data: UpdateDepartmentSettingsRequest }
  >({
    mutationFn: ({ id, data }) => departmentService.updateSettings(id, data),
    onSuccess: (res) => {
      invalidateDepartments(queryClient);
      toastMutationSuccess(res, { fallbackMessage: "تم تحديث إعدادات الدائرة بنجاح" });
    },
    onError: (error) => handleApiError(error),
  });
}

export function useAssignManager() {
  const queryClient = useQueryClient();
  return useMutation<
    Department,
    Error,
    { id: string; data: AssignManagerRequest }
  >({
    mutationFn: ({ id, data }) => departmentService.assignManager(id, data),
    onSuccess: (res) => {
      invalidateDepartments(queryClient);
      toastMutationSuccess(res, {
        fallbackMessage: "تم تعيين مدير الدائرة بنجاح",
      });
    },
    onError: (error) => handleApiError(error),
  });
}
